// Icon — v1.9 P0 全局图标统一封装
// 用 lucide-react (ISC 协议, 1500+ 图标, tree-shakable)
// 默认 strokeWidth 1.5 (跟 wabi-sabi 细线美学一致)
// 颜色: 全部 inherit currentColor
//
// 用法:
//   <Icon name="circle-dot" />                 // sidebar nav icon (20px)
//   <Icon name="mail" size="sm" />             // button 内 icon (16px)
//   <Icon name="check" size="xs" strokeWidth={2} />  // status badge (12px)
//
// 兼容 emoji 字符串 — 自动查 ICON_MAP 转 icon (向后兼容旧代码)
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  ArrowUpRight,
  BarChart3,
  Bell,
  BookOpen,
  Briefcase,
  Building2,
  Cake,
  CalendarDays,
  CalendarPlus,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleDollarSign,
  CircleDot,
  CircleHelp,
  CircleX,
  ClipboardList,
  Clock,
  Cloud,
  Copy,
  Crosshair,
  Database,
  Diamond,
  Download,
  Dumbbell,
  ExternalLink,
  Eye,
  Factory,
  FileText,
  Filter,
  Flag,
  Gift,
  Globe,
  Globe2,
  Hand,
  Heart,
  HelpCircle,
  Hexagon,
  Home,
  Hourglass,
  Info,
  Key,
  LayoutDashboard,
  LayoutGrid,
  Lightbulb,
  Link,
  List,
  ListChecks,
  Lock,
  LockOpen,
  type LucideIcon,
  Mail,
  MapPin,
  Menu,
  MessageSquare,
  Minus,
  Package,
  Paperclip,
  PartyPopper,
  Pencil,
  Phone,
  Pin,
  Plane,
  Plus,
  Power,
  Printer,
  Receipt,
  RefreshCw,
  Rocket,
  RotateCcw,
  Save,
  Search,
  SearchX,
  Send,
  ServerCrash,
  Settings,
  Settings2,
  Share2,
  ShoppingCart,
  Shuffle,
  Siren,
  Sparkles,
  Star,
  Sun,
  SunMoon,
  Tag,
  Target,
  Tent,
  ThumbsDown,
  ThumbsUp,
  Timer,
  Trash2,
  Trees,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Trophy,
  Truck,
  Unlock,
  Upload,
  User,
  UserPlus,
  Users,
  Wallet,
  Wand2,
  Wrench,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import { type ComponentProps, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;

const SIZE_MAP: Record<Exclude<IconSize, number>, number> = {
  xs: 12,
  sm: 16,
  md: 20,
  lg: 24,
  xl: 32,
};

/** kebab-case → lucide 组件 映射表 (核心) */
const ICON_REGISTRY: Record<string, LucideIcon> = {
  // nav / 通用 UI
  'circle-dot': CircleDot,
  circle: Circle,
  diamond: Diamond,
  menu: Menu,
  list: List,
  sparkles: Sparkles,
  crosshair: Crosshair,
  mail: Mail,
  settings: Settings,
  'settings-2': Settings2,
  power: Power,
  home: Home,
  'layout-dashboard': LayoutDashboard,
  'arrow-up-right': ArrowUpRight,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'chevron-down': ChevronDown,
  'chevron-up': ChevronUp,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  x: X,
  check: Check,
  'check-circle': CheckCircle2,
  'check-circle-2': CheckCircle2,
  'circle-check': CircleCheck,
  'x-circle': XCircle,
  'circle-x': CircleX,
  plus: Plus,
  minus: Minus,
  'arrow-up-down': ArrowUpDown,
  'rotate-ccw': RotateCcw,
  'rotate-cw': RefreshCw,
  'refresh-cw': RefreshCw,
  'trash-2': Trash2,
  edit: Pencil,
  pencil: Pencil,
  copy: Copy,
  upload: Upload,
  download: Download,
  send: Send,
  search: Search,
  'search-x': SearchX,
  'server-crash': ServerCrash,
  cloud: Cloud,
  database: Database,
  activity: Activity,
  filter: Filter,
  link: Link,
  'external-link': ExternalLink,
  eye: Eye,
  flag: Flag,
  unlock: Unlock,
  lock: Lock,
  'lock-open': LockOpen,
  'alert-triangle': AlertTriangle,
  'alert-circle': AlertCircle,
  'circle-alert': CircleAlert,
  'triangle-alert': TriangleAlert,
  info: Info,
  'help-circle': HelpCircle,
  'circle-help': CircleHelp,
  siren: Siren,
  'map-pin': MapPin,
  pin: Pin,
  package: Package,
  hexagon: Hexagon,
  'clipboard-list': ClipboardList,
  'bar-chart-3': BarChart3,
  'bar-chart-2': BarChart3,
  'file-text': FileText,
  'book-open': BookOpen,
  trophy: Trophy,
  target: Target,
  dumbbell: Dumbbell,
  'thumbs-up': ThumbsUp,
  'thumbs-down': ThumbsDown,
  star: Star,
  heart: Heart,
  'party-popper': PartyPopper,
  rocket: Rocket,
  'message-square': MessageSquare,
  phone: Phone,
  'calendar-days': CalendarDays,
  clock: Clock,
  bell: Bell,
  lightbulb: Lightbulb,
  timer: Timer,
  hourglass: Hourglass,
  tag: Tag,
  key: Key,
  receipt: Receipt,
  'circle-dollar-sign': CircleDollarSign,
  paperclip: Paperclip,
  user: User,
  'user-plus': UserPlus,
  users: Users,
  globe: Globe,
  'globe-2': Globe2,
  factory: Factory,
  truck: Truck,
  shopping: ShoppingCart,
  'shopping-cart': ShoppingCart,
  tent: Tent,
  briefcase: Briefcase,
  hand: Hand,
  building: Building2,
  plane: Plane,
  printer: Printer,
  save: Save,
  share: Share2,
  shuffle: Shuffle,
  wrench: Wrench,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  zap: Zap,
  sun: Sun,
  'sun-moon': SunMoon,
  'wand-2': Wand2,
  // v1.32 新增 — 工作台 + 节日 + 设置 tab
  'layout-grid': LayoutGrid,
  'list-checks': ListChecks,
  wallet: Wallet,
  gift: Gift,
  trees: Trees,
  cake: Cake,
  'calendar-plus': CalendarPlus,
};

/**
 * emoji → icon name 兼容表
 * 当调用方传 emoji 字符串时, 自动转 icon (让现有代码渐进迁移)
 * 国旗不进入此表 — 保留 emoji
 */
const EMOJI_TO_ICON: Record<string, string> = {
  '◉': 'circle-dot',
  '◐': 'circle',
  '◇': 'diamond',
  '☰': 'menu',
  '✦': 'sparkles',
  '⌖': 'crosshair',
  '✉': 'mail',
  '⚙': 'settings',
  '⏻': 'power',
  '⌂': 'home',
  '↗': 'arrow-up-right',
  '✕': 'x',
  '✗': 'x',
  '✓': 'check',
  '◯': 'circle',
  '▾': 'chevron-down',
  '▴': 'chevron-up',
  '←': 'arrow-left',
  '→': 'arrow-right',
  '−': 'minus',
  '+': 'plus',
  // 状态
  '🚨': 'siren',
  '⚠': 'alert-triangle',
  '⚠️': 'alert-triangle',
  ℹ: 'info',
  '🔴': 'circle-alert',
  '✅': 'check-circle-2',
  '💡': 'lightbulb',
  // 业务 / 装饰
  '📍': 'map-pin',
  '📦': 'package',
  '📌': 'pin',
  '📋': 'clipboard-list',
  '📊': 'bar-chart-3',
  '📚': 'book-open',
  '🏆': 'trophy',
  '🎯': 'target',
  '💪': 'dumbbell',
  '👍': 'thumbs-up',
  '👎': 'thumbs-down',
  '🔍': 'search',
  '🔗': 'link',
  '🔄': 'refresh-cw',
  '🔀': 'shuffle',
  '📥': 'download',
  '📤': 'upload',
  '🚀': 'rocket',
  '💬': 'message-square',
  '💌': 'mail',
  '💰': 'circle-dollar-sign',
  '💼': 'briefcase',
  '🛠': 'wrench',
  '🎪': 'tent',
  '🛒': 'shopping-cart',
  '🏭': 'factory',
  '🌐': 'globe',
  '🌍': 'globe-2',
  '✋': 'hand',
  '📧': 'mail',
  '📅': 'calendar-days',
  '📞': 'phone',
  '⏰': 'clock',
  '🔔': 'bell',
  '⭐': 'star',
  '❤️': 'heart',
  '🎉': 'party-popper',
  '🔒': 'lock',
  '🔓': 'lock-open',
  '⚡': 'zap',
  '📎': 'paperclip',
  '✏️': 'pencil',
  '📝': 'file-text',
  '⏱': 'timer',
  '⏳': 'hourglass',
  '🏷': 'tag',
  '🔑': 'key',
  '🧾': 'receipt',
  '🔧': 'settings-2',
  '!': 'alert-circle',
  '!!': 'alert-circle',
};

/** 去除 emoji 字符串中的 variation selector-16 (U+FE0F, 控制 emoji 彩色 vs 单色渲染) */
// biome 的 noMisleadingCharacterClass 不允许字符类同时包含基础字符和 combining char,
// 所以只能用单字符匹配。FE0F 是最常见的 VS-16; FE00-FE0E 是其他 VS, 暂不处理 (实际渲染影响小)
function stripVS(s: string): string {
  return s.replace(/\u{FE0F}/gu, '');
}

export interface IconProps extends Omit<ComponentProps<'svg'>, 'name'> {
  /** kebab-case 名字, e.g. "circle-dot", "mail"; 也支持直接传 emoji 字符串 */
  name: string;
  /** Tailwind size token 或具体像素; 默认 'md' (20px) */
  size?: IconSize;
  /** 描边宽度; 默认 1.5 (wabi-sabi 细线) */
  strokeWidth?: number;
  /** 自定义 className (会跟默认 merge) */
  className?: string;
}

/**
 * Icon — 统一图标组件
 * - 全部 inherit currentColor (color 不写死)
 * - 默认 strokeWidth 1.5 (细线 wabi-sabi 风格)
 * - 接受 emoji 字符串 (向后兼容)
 */
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, size = 'md', strokeWidth = 1.5, className, ...rest },
  ref,
) {
  const resolved = stripVS(name);
  const key = EMOJI_TO_ICON[resolved] ?? resolved;
  const LucideIcon = ICON_REGISTRY[key];

  if (!LucideIcon) {
    if (import.meta.env.DEV) {
      console.warn(
        `[Icon] unknown name: "${name}". Add to ICON_REGISTRY in apps/web/src/components/Icon.tsx`,
      );
    }
    return null;
  }

  const sizePx = typeof size === 'number' ? size : SIZE_MAP[size];

  return (
    <LucideIcon
      ref={ref}
      size={sizePx}
      strokeWidth={strokeWidth}
      className={twMerge('inline-block shrink-0 align-middle', className)}
      aria-hidden={rest['aria-label'] ? undefined : true}
      {...rest}
    />
  );
});
